import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon.js';
import { Checkbox } from '../ui/Checkbox.js';
import { Pressable } from '../ui/Pressable.js';

export type TimeBand = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'older_2_3_months' | 'older';

const BAND_ORDER: TimeBand[] = ['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older'];

const BAND_I18N_KEYS: Record<TimeBand, string> = {
  today: 'common.timeBands.today',
  yesterday: 'common.timeBands.yesterday',
  this_week: 'common.timeBands.thisWeek',
  this_month: 'common.timeBands.thisMonth',
  older_2_3_months: 'common.timeBands.older2_3Months',
  older: 'common.timeBands.older',
};

export function getTimeBand(isoTimestamp: string): TimeBand {
  const now = Date.now();
  const DAY = 86_400_000;
  const ts = new Date(isoTimestamp.endsWith('Z') ? isoTimestamp : isoTimestamp + 'Z').getTime();
  const ms = now - ts;
  if (ms < DAY) return 'today';
  if (ms < 2 * DAY) return 'yesterday';
  if (ms < 7 * DAY) return 'this_week';
  if (ms < 30 * DAY) return 'this_month';
  if (ms < 90 * DAY) return 'older_2_3_months';
  return 'older';
}

interface BandGroup<T> {
  band: TimeBand;
  items: T[];
}

interface TimeBandedListProps<T, Id extends number | string = number> {
  items: T[];
  getTimestamp: (item: T) => string;
  getId: (item: T) => Id;
  renderItem: (item: T) => React.ReactNode;
  isUnread?: (item: T) => boolean;
  checkedIds: Set<Id>;
  onBandCheck: (ids: Id[], allChecked: boolean) => void;
  /** If provided, bands in this set are collapsed */
  collapsedBands?: Set<string>;
  onToggleBand?: (band: string) => void;
  /** Sentinel element rendered after the last item (for infinite scroll) */
  loadMoreSentinel?: React.ReactNode;
  emptyState?: React.ReactNode;
}

export function TimeBandedList<T, Id extends number | string = number>({
  items,
  getTimestamp,
  getId,
  renderItem,
  isUnread,
  checkedIds,
  onBandCheck,
  collapsedBands: externalCollapsed,
  onToggleBand: externalToggle,
  loadMoreSentinel,
  emptyState,
}: TimeBandedListProps<T, Id>) {
  const { t } = useTranslation();
  const [internalCollapsed, setInternalCollapsed] = useState<Set<string>>(new Set());

  const collapsed = externalCollapsed ?? internalCollapsed;
  const toggle = externalToggle ?? ((band: string) => {
    setInternalCollapsed(prev => {
      const next = new Set(prev);
      next.has(band) ? next.delete(band) : next.add(band);
      return next;
    });
  });

  const groups = useMemo<BandGroup<T>[]>(() => {
    const map = new Map<TimeBand, T[]>();
    for (const item of items) {
      const band = getTimeBand(getTimestamp(item));
      if (!map.has(band)) map.set(band, []);
      map.get(band)!.push(item);
    }
    return BAND_ORDER.filter(b => map.has(b)).map(b => ({ band: b, items: map.get(b)! }));
  }, [items, getTimestamp]);

  if (items.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <>
      {groups.map(({ band, items: bandItems }) => {
        const isCollapsed = collapsed.has(band);
        const ids = bandItems.map(getId);
        const checkedInBand = ids.filter(id => checkedIds.has(id));
        const allChecked = checkedInBand.length === ids.length && ids.length > 0;
        const someChecked = checkedInBand.length > 0 && !allChecked;
        const unreadCount = isUnread ? bandItems.filter(isUnread).length : 0;

        return (
          <React.Fragment key={band}>
            {/* Band header */}
            <div
              className="flex items-center gap-2 py-1.5 text-xs font-semibold uppercase tracking-wider"
              style={{
                background: 'var(--evo-bg-2)',
                borderBottom: '1px solid var(--evo-stroke)',
                borderTop: '1px solid var(--evo-stroke)',
                color: 'var(--evo-fg-3)',
                userSelect: 'none',
                paddingLeft: 'var(--evo-list-pad-x)',
                paddingRight: '12px',
              }}
            >
              {/* Checkbox ya corta su propia propagación (ver Checkbox.tsx) — un
                  solo wrapper visual, un solo disparo real, sin duplicar la
                  llamada acá afuera como antes (eso era lo que la rompía). */}
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                <Checkbox
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={() => onBandCheck(ids, allChecked)}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Pressable fullWidth onPress={() => toggle(band)}>
                  <div className="flex items-center gap-1.5">
                    <span
                      style={{
                        display: 'inline-flex',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                        transition: 'transform 0.15s',
                        flexShrink: 0,
                      }}
                    >
                      <Icon name="chevron-down" size={10} strokeWidth={2} />
                    </span>
                    <span className="capitalize flex-1">{t(BAND_I18N_KEYS[band])}</span>
                    <span style={{ fontWeight: 400, textTransform: 'none' }}>({bandItems.length})</span>
                  </div>
                </Pressable>
              </div>
              {unreadCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold shrink-0"
                  style={{ background: 'var(--evo-cyan)', color: '#0a0a0a', textTransform: 'none', letterSpacing: 0 }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
            {/* Band items */}
            <div
              style={{
                overflow: 'hidden',
                transition: 'max-height 200ms ease-out',
                maxHeight: isCollapsed ? 0 : '100000px',
              }}
            >
              {bandItems.map(item => (
                <React.Fragment key={getId(item)}>
                  {renderItem(item)}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        );
      })}
      {loadMoreSentinel}
    </>
  );
}
